import * as React from 'react';
import { useCallback } from 'react';
import * as _ from 'lodash';
import * as Papa from 'papaparse';
import { useDropzone, DropEvent, DropzoneInputProps } from 'react-dropzone';
import { DropZoneProps, CsvLimit } from './DropZoneProps.interface';
import FileZone from '../FileZone/FileZone';
import {
  FileType,
  formatDecimal,
  formatFileSizeRounded,
  getUuidV4,
  Logging,
} from '../..';
import './dropzone.scss';

interface SyncScanResult {
  resultType: 'CLEAN' | 'INFECTED';
}

const BYTES_PER_MEGABYTE = 1048576;

const maxVirusScanFileSize = BYTES_PER_MEGABYTE * 30; // Only Scan Files smaller than 30MB

const DropZone = ({
  dragTextDescription,
  dropTextDescription,
  showRestrictions,
  dragDropImage,
  maxNumberFiles,
  maxFileSize,
  maxFilesSize,
  csvLimits,
  allowedFileTypes,
  dropFiles,
  setDropFiles,
  statusMap,
  setStatusMap,
  setFilesError,
  virusScanningUrl = null,
}: DropZoneProps): JSX.Element => {
  const [pendingFiles, setPendingFiles] = React.useState([]);

  // Remove the File from the Array and return the modified array
  const removeFileFromArray = (file: File, files: File[]): File[] => {
    const index: number = files.indexOf(file);

    if (index !== -1) {
      files.splice(index, 1);
    }
    return files;
  };

  const updateFilesError = (): void => {
    let error: string = null;
    let totalFileSize = 0;

    // Existing Files
    dropFiles.forEach((file: File) => {
      totalFileSize += file.size;
    });

    if (maxNumberFiles && (dropFiles.length > maxNumberFiles)) {
      Logging.Info('Max number of Files reached', dropFiles.length, maxNumberFiles);
      error = maxNumberFiles > 1
        ? `The maximum number of files is reached. Only ${maxNumberFiles} files are allowed.`
        : 'The maximum number of files is reached. Only 1 file is allowed.';
    }

    if (maxFilesSize && (totalFileSize >= maxFilesSize)) {
      Logging.Info('Max size of Files reached', totalFileSize, maxFilesSize);
      error = `The maximum size for all files is reached. The total files size must be smaller than ${formatFileSizeRounded(maxFilesSize)}.`;
    }

    Logging.Debug('Update Files Error', dropFiles, error);

    setFilesError(error);
  };

  // Load the File Asynchronously.  Update the Status on Error or Success
  const loadFile = (file: File): void => {
    const reader = new FileReader();

    // Reader Abort
    reader.onabort = (): void => {
      const localDataMap: Map<string, boolean | string> = _.cloneDeep(statusMap);
      localDataMap.set(file.name, 'Loading of file was Aborted');
      Logging.Debug('File Abort', file.name);
      setStatusMap(localDataMap);

      let localPendingFiles: File[] = _.cloneDeep(pendingFiles);
      localPendingFiles = removeFileFromArray(file, localPendingFiles);
      setPendingFiles(localPendingFiles);
    };
    // Reader Error
    reader.onerror = (): void => {
      const localDataMap: Map<string, boolean | string> = _.cloneDeep(statusMap);
      localDataMap.set(file.name, 'Loading of file Error');
      Logging.Debug('File Error', file.name);
      setStatusMap(localDataMap);

      let localPendingFiles: File[] = _.cloneDeep(pendingFiles);
      localPendingFiles = removeFileFromArray(file, localPendingFiles);
      setPendingFiles(localPendingFiles);
    };

    // Reader Success
    reader.onload = (): void => {
      const binaryStr = reader.result;

      const columns: string[] = [];
      const rows: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
      const parseErrors: string[] = [];

      Papa.parse(file, {
        dynamicTyping: true, // Converts numeric/boolean data
        header: true, // Key data by field name instead of index/position
        worker: true, // Run in a seperate Thread. Will process a little slower
        skipEmptyLines: true,
        step: (row: Papa.ParseResult<any>) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          // Grab the Columns from the First Row
          if (!columns.length) {
            Logging.Debug('CSV File Columns', row.meta.fields ? row.meta.fields : row.data);

            // Header is Used
            if (row.meta.fields) {
              row.meta.fields.forEach((field: string) => {
                columns.push(field.trim());
              });
            }
          }

          rows.push(row.data);
          Logging.Trace('CSV Row:', rows.length, row.data);

          if (row.errors && row.errors.length > 0) {
            Logging.Info('CSV Row Error @', rows.length, row.errors);
            // Parse and Save the Errors
            row.errors.forEach((error: Papa.ParseError) => {
              const errorMessage = `Row ${rows.length}: ${error.message}`;
              parseErrors.push(errorMessage);
            });
          }
        },
        complete: (results: Papa.ParseResult<any>) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          Logging.Info('CSV File Rows', rows.length);

          let csvHasError = false;
          let csvError: string = null;

          if (parseErrors.length > 0) {
            csvError = parseErrors.join('\n');
            csvHasError = true;
          } else {
            csvLimits.forEach((limit: CsvLimit) => {
              if (rows.length > 0) {
                // Check if the Limits Column is defined
                if (columns.indexOf(limit.column) >= 0) {
                  const distinctValues: string[] = [];

                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  rows.forEach((row: any) => {
                    let value = row[limit.column];

                    // The Limit value is Null.  Generate a Unique Key for it so that it will be counted
                    if (value === null) {
                      value = getUuidV4();
                    }

                    // Only count Distinct values
                    if (distinctValues.indexOf(value) === -1) {
                      distinctValues.push(value);
                    }
                  });

                  Logging.Debug('CSV Distinct File Rows', distinctValues.length);

                  // Minimum Occurrences Failed
                  if ((limit.check === 'Minimum') && (distinctValues.length < limit.occurrences)) {
                    csvHasError = true;
                    csvError = `${limit.error} A total of ${formatDecimal(distinctValues.length)} were found.`;
                    Logging.Info('CSV Minimum Limit Failed:', limit, distinctValues);
                  }

                  // Maximum Occurrences Failed
                  if ((limit.check === 'Maximum') && (distinctValues.length > limit.occurrences)) {
                    csvHasError = true;
                    csvError = `${limit.error} A total of ${formatDecimal(distinctValues.length)} were found.`;
                    Logging.Info('CSV Maximum Limit Failed:', limit, distinctValues);
                  }
                } else {
                  csvHasError = true;
                  csvError = `The Header does not contain the '${limit.column}' column.`;
                }
              } else {
                csvHasError = true;
                csvError = 'The File does not contain any data rows.';
              }
            });
          }

          const localDataMap: Map<string, boolean | string> = _.cloneDeep(statusMap);

          // CSV Has Error
          if (csvHasError) {
            localDataMap.set(file.name, csvError);
          } else {
            // Store the File as the Key and the Data as the Value in the Dictionary
            localDataMap.set(file.name, true);
          }

          setStatusMap(localDataMap);

          let localPendingFiles: File[] = _.cloneDeep(pendingFiles);
          localPendingFiles = removeFileFromArray(file, localPendingFiles);
          setPendingFiles(localPendingFiles);
        },
      });
    };

    reader.readAsArrayBuffer(file);
  };

  // Get the File Extension for the associated file type
  const getExtensionForType = (fileType: string): string => {
    switch (fileType.toLowerCase()) {
      case FileType.Csv:
        return 'CSV';
      case FileType.Csv2:
        return 'CSV';
      case FileType.Zip:
        return 'ZIP';
      case FileType.Zip2:
        return 'ZIP';
      case FileType.Pdf:
        return 'PDF';
      case FileType.Bmp:
        return 'BMP';
      case FileType.Jpg:
        return 'JPG';
      case FileType.Png:
        return 'PNG';
      case FileType.Gif:
        return 'GIF';
      case FileType.WebP:
        return 'WEBP';
      default:
        return 'Unknown';
    }
  };

  // Update the File Status
  const updateFileStatus = (fileName: string, fileStatus: boolean | string): void => {
    // Use the Current Status to prevent a Stale State
    setStatusMap((status) => {
      const localDataMap: Map<string, boolean | string> = _.cloneDeep(status);
      localDataMap.set(fileName, fileStatus);
      return localDataMap;
    });
  };

  // Scan the File for Virus Asynchronously
  const virusScanFileAsynchronously = (file: File): void => {
    Logging.Info('Virus Scanning:', file.name, file.size);

    let scanningStart: Date = null;
    const request = new XMLHttpRequest();
    const formData = new FormData();

    // Make Asynchronous Request
    request.open('POST', virusScanningUrl, true);
    request.onreadystatechange = () => {
      if (request.readyState === XMLHttpRequest.DONE) {
        if (request.status === 200) {
          const result: SyncScanResult = JSON.parse(request.responseText);

          const scanningEnd: Date = new Date();
          const timeDiff = scanningStart ? scanningEnd.getTime() - scanningStart.getTime() : null;
          Logging.Info(`Virus Scan in ${timeDiff} ms`, file.name, file.size, result?.resultType);

          if (result.resultType === 'CLEAN') {
            // Check if the File must be parsed
            const parseFile = csvLimits && (csvLimits.length > 0);

            if (parseFile) {
              // Use the Current Status to prevent a Stale State
              setPendingFiles((files) => {
                const localPendingFiles: File[] = _.cloneDeep(files);
                localPendingFiles.push(file);
                return localPendingFiles;
              });
            } else {
              // File Status is CLEAN
              updateFileStatus(file.name, true);
            }
          } else if (result.resultType === 'INFECTED') {
            // File Status is INFECTED
            updateFileStatus(file.name, 'File is infected with a virus!');
          } else {
            // File Status is INFECTED
            updateFileStatus(file.name, `Unknown virus scan result: ${result}`);
          }
        } else {
          Logging.Info('Virus Scan Error:', file.name, request.status, request.statusText);

          updateFileStatus(file.name, 'Unable to virus scan the File');
        }
      }
    };
    request.ontimeout = () => {
      Logging.Info('Virus Scan Timeout:', file.name);

      updateFileStatus(file.name, 'Unable to virus scan the File - Timeout');
    };

    formData.append('file', file);
    request.send(formData);

    scanningStart = new Date();
  };

  // Load Files
  const loadFiles = (
    dropFilesAccepted: File[],
    dropFilesRejected: File[],
    dropFilesRejectedReason: Map<string, string>,
  ): void => {
    // In Replace Mode, replace the files as opposed to adding them
    const localDropFiles: File[] = _.cloneDeep(dropFiles);
    const localPendingFiles: File[] = _.cloneDeep(pendingFiles);

    // Accepted Files
    dropFilesAccepted.forEach((file: File) => {
      // Check if the Files already exist in the Drop File Array
      if (localDropFiles.find((x: File) => x.name === file.name) == null) {
        localDropFiles.push(file);
      }

      // Check if the Files already exist in the Pending File Array
      if (localPendingFiles.find((x: File) => x.name === file.name) == null) {
        // Check if File must be virus Scanned and if it is small enough
        if (virusScanningUrl && (file.size < maxVirusScanFileSize)) {
          // Scan the File for Virus
          virusScanFileAsynchronously(file);
        } else {
          // Check if the File must be parsed
          const parseFile = csvLimits && (csvLimits.length > 0);

          // CSV File needs to be parsed for errors
          if (parseFile) {
            localPendingFiles.push(file);
          } else {
            // File Status is OK
            updateFileStatus(file.name, true);
          }
        }
      }
    });

    // The Pending Files will be parsed after Virus Scanning
    if (virusScanningUrl === null) {
      setPendingFiles(localPendingFiles);
    }

    // Rejected Files
    dropFilesRejected.forEach((file: File) => {
      // Check if the Files already exist in the Drop File Array
      if (localDropFiles.find((x: File) => x.name === file.name) == null) {
        localDropFiles.push(file);
      }

      // Set the Error Reason in the Map
      updateFileStatus(file.name, dropFilesRejectedReason.get(file.name));
    });

    setDropFiles(localDropFiles);
  };

  // Accept the drop files
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: File[], event: DropEvent) => {
    const dropFilesAccepted: File[] = [];
    const dropFilesRejected: File[] = [];
    const dropFilesRejectedReason: Map<string, string> = new Map<string, string>();
    const fileTypes: string[] = [];

    allowedFileTypes.forEach((fileType: string) => {
      const extension: string = getExtensionForType(fileType);

      // Dont add Duplicates
      if (fileTypes.indexOf(extension) === -1) {
        fileTypes.push(extension);
      }
    });

    // Loop through the Files and Check for restrictions
    acceptedFiles.forEach((file: File) => {
      if (maxFileSize && (file.size > maxFileSize)) {
        Logging.Debug('File is to Big', file, maxFileSize);
        dropFilesRejected.push(file);
        dropFilesRejectedReason.set(
          file.name,
          `The file is too big. Only files smaller than ${formatFileSizeRounded(maxFileSize)} are allowed.`,
        );
      } else if (allowedFileTypes.length && allowedFileTypes.indexOf(file.type) === -1) {
        Logging.Debug('File is of incorrect Type', file, allowedFileTypes);
        dropFilesRejected.push(file);
        dropFilesRejectedReason.set(
          file.name,
          // eslint-disable-next-line max-len
          `The file is the incorrect type. Allowed type${fileTypes.length > 1 ? 's' : ''}: ${fileTypes.join(', ')}`,
        );
      } else {
        dropFilesAccepted.push(file);
      }
    });

    loadFiles(dropFilesAccepted, dropFilesRejected, dropFilesRejectedReason);
  }, [dropFiles, statusMap]);

  React.useEffect(() => {
    // Check if files are Pending to be Loaded
    if (pendingFiles.length > 0) {
      // Get the First File to be loaded
      const file: File = pendingFiles[0];

      // Load the File
      loadFile(file);
    }
  }, [pendingFiles]);

  React.useEffect(() => {
    updateFilesError();
  }, [dropFiles]);

  // Delete the File
  const onDeleteFile = (file: File): void => {
    let localDropFiles: File[] = _.cloneDeep(dropFiles);
    let localPendingFiles: File[] = _.cloneDeep(pendingFiles);
    const localDataMap: Map<string, boolean | string> = _.cloneDeep(statusMap);

    // Remove the File from the File Arrays
    localDropFiles = removeFileFromArray(file, localDropFiles);
    localPendingFiles = removeFileFromArray(file, localPendingFiles);

    // Delete the File from the Data Dictionary
    localDataMap.delete(file.name);

    // Update the State
    setDropFiles(localDropFiles);
    setPendingFiles(localPendingFiles);
    setStatusMap(localDataMap);
  };

  const getAcceptedFileTypes = (): string => {
    const types: string[] = [];

    allowedFileTypes.forEach((allowedFileType: string) => {
      // For Chrome Browser only
      if (allowedFileType === FileType.Csv) {
        // Browser inteprets CSV File Type as Excel file Type.  Fix in case user Double click to Browse for file
        types.push('.csv');
      } else {
        types.push(allowedFileType);
      }
    });

    return types.join(',');
  };

  // Create a list of Restrictions
  const createRestrictions = (): string[] => {
    const restrictions: string[] = [];

    const fileTypes: string[] = [];

    if (csvLimits) {
      csvLimits.forEach((limit: CsvLimit) => {
        if (limit.showInRestrictions) {
          restrictions.push(limit.error);
        }
      });
    }

    allowedFileTypes.forEach((fileType: string) => {
      const extension: string = getExtensionForType(fileType);

      // Dont add Duplicates
      if (fileTypes.indexOf(extension) === -1) {
        fileTypes.push(extension);
      }
    });

    if (maxNumberFiles) {
      restrictions.push(`Maximum of ${maxNumberFiles} file${maxNumberFiles > 1 ? 's' : ''}.`);
    }

    if (maxFileSize) {
      restrictions.push(`Maximum of ${formatFileSizeRounded(maxFileSize)} per file.`);
    }

    if (maxFilesSize) {
      restrictions.push(`Maximum of ${formatFileSizeRounded(maxFilesSize)} for all files.`);
    }

    if (allowedFileTypes.length) {
      restrictions.push(`Only the following file type${fileTypes.length > 1 ? 's' : ''}: ${fileTypes.join(', ')}`);
    } else {
      restrictions.push('All file types allowed.');
    }

    return restrictions;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const dragText: string = dragTextDescription || 'Drag the files here, or click to select files.';
  const dropText: string = dropTextDescription || 'Drop the files here.';
  const multipleFiles: boolean = maxNumberFiles > 0;
  const acceptedFileTypesList: string = getAcceptedFileTypes();
  const restrictions: string[] = createRestrictions();

  const hasFiles: boolean = dropFiles.length > 0;

  // Files have been dropped before.  Show the Dropped files
  if (hasFiles) {
    return (
      <div className="dropzoneContentMain">
        <div className="dropzoneDropRoot" {...getRootProps()}>
          {/* Use getInputProps () to provide props to input.  This does not seem to work. */}
          <input {...getInputProps({ accept: acceptedFileTypesList, multiple: multipleFiles })} />
          <FileZone
            files={dropFiles}
            statusMap={statusMap}
            onDeleteFile={onDeleteFile}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="dropzoneContentMain">
      <div className="dropzoneDropRoot" {...getRootProps()}>
        <div className="dropzoneContentDrop">
          {/* Use getInputProps () to provide props to input.  This does not seem to work. */}
          <input {...getInputProps({ accept: acceptedFileTypesList, multiple: multipleFiles })} />
          {
            isDragActive
              ? (
                <div className="dropzoneDragDropText">
                  <img src={dragDropImage} alt="Cloud" className="dropzoneDropImage" />
                  <p>{dropText}</p>
                </div>
              )
              : (
                <div className="dropzoneDragDropText">
                  <img src={dragDropImage} alt="Cloud" className="dropzoneDropImage" />
                  <p>{dragText}</p>
                </div>
              )
          }
          <div className="dropzoneRestrictionsList">
            <p>{showRestrictions && restrictions.join(' ')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DropZone;
